import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal, flushSync } from "react-dom";
import { Printer } from "@phosphor-icons/react";
import { defaultOrderPrintSettings, formatCny, ORDER_PRINT_ITEMS_PER_DOCUMENT, orderPrintSettingsSchema, type OrderPrintBatch, type SavedOrderPrintSettings } from "@longjie/shared";
import { Button, Modal } from "./components";
import type { Order } from "./types";
import { formatDateTime, fulfillmentLabel } from "./ui-utils";
import "./order-print.css";
import { request } from "./api";
import { OrderPrintSettingsEditor } from "./OrderPrintSettingsEditor";

const ITEMS_PER_PAGE = ORDER_PRINT_ITEMS_PER_DOCUMENT;
type PrintPage = { items: Order["items"]; note: string };

function initialPages(order: Order): PrintPage[] {
  const pages: PrintPage[] = [];
  for (let index = 0; index < order.items.length; index += ITEMS_PER_PAGE) {
    pages.push({ items: order.items.slice(index, index + ITEMS_PER_PAGE), note: "" });
  }
  if (!pages.length) pages.push({ items: [], note: "" });
  pages[pages.length - 1]!.note = order.note;
  return pages;
}

export function OrderPrintPreview({ order, onClose, canEditSettings = false, initialSettings }: {
  order: Order; onClose: () => void; canEditSettings?: boolean; initialSettings?: SavedOrderPrintSettings;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [saved, setSaved] = useState(initialSettings ?? { settings: defaultOrderPrintSettings, revision: 0 });
  const [draft, setDraft] = useState(saved.settings);
  const [ready, setReady] = useState(Boolean(initialSettings));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState("");
  const printBusy = useRef(false);
  const printRequest = useRef<{ key: string; idempotencyKey: string } | null>(null);
  const [printBatch, setPrintBatch] = useState<{ key: string; batch: OrderPrintBatch } | null>(null);
  const template = editing ? draft : saved.settings;
  const [pagination, setPagination] = useState(() => ({ source: order, template, pages: initialPages(order), overflow: false }));
  const currentPagination = pagination.source === order && pagination.template === template;
  const pages = currentPagination ? pagination.pages : initialPages(order);
  const overflow = currentPagination && pagination.overflow;
  const printPages = pages.map((page) => ({ itemIds: page.items.map((item) => item.id), totalCents: page.items.reduce((sum, item) => sum + item.lineTotalCents, 0), note: page.note }));
  const printKey = JSON.stringify({ orderId: order.id, template, pages: printPages });
  const documents = printBatch?.key === printKey ? printBatch.batch.documents : undefined;

  async function printDocuments() {
    if (printBusy.current || !ready || editing || overflow) return;
    printBusy.current = true;
    setPrinting(true); setPrintError(""); setPrintBatch(null);
    try {
      if (printRequest.current?.key !== printKey) printRequest.current = { key: printKey, idempotencyKey: crypto.randomUUID() };
      const batch = await request<OrderPrintBatch>(`/api/v1/admin/orders/${order.id}/print-batches`, {
        method: "POST", body: JSON.stringify({ idempotencyKey: printRequest.current.idempotencyKey, pages: printPages }),
      });
      if (batch.documents.length !== pages.length || batch.documents.some((document, index) => !/^\d{11,}$/.test(document.printNo) || document.totalCents !== printPages[index]!.totalCents)) {
        throw new Error("打印单号与当前内容不一致，请重新打开打印预览");
      }
      // Commit the allocated numbers before the browser takes its print snapshot.
      flushSync(() => setPrintBatch({ key: printKey, batch }));
      window.print();
      printRequest.current = null;
    } catch (error) {
      setPrintError(error instanceof Error ? error.message : "打印单号生成失败，请重试");
    } finally { printBusy.current = false; setPrinting(false); }
  }

  useEffect(() => {
    if (initialSettings) return;
    let active = true;
    request<SavedOrderPrintSettings>("/api/v1/admin/order-print-settings").then((result) => {
      if (!active) return;
      const settings = orderPrintSettingsSchema.parse(result.settings);
      setSaved({ ...result, settings }); setDraft(settings); setReady(true);
    }).catch((error: unknown) => { if (active) setSettingsError(error instanceof Error ? error.message : "打印设置加载失败"); });
    return () => { active = false; };
  }, [initialSettings]);

  async function reloadSettings() {
    setSettingsError(""); setReady(false);
    try {
      const result = await request<SavedOrderPrintSettings>("/api/v1/admin/order-print-settings");
      const settings = orderPrintSettingsSchema.parse(result.settings);
      setSaved({ ...result, settings }); setDraft(settings); setEditing(false); setReady(true);
    } catch (error) { setSettingsError(error instanceof Error ? error.message : "打印设置加载失败"); }
  }

  async function saveSettings() {
    const parsed = orderPrintSettingsSchema.safeParse(draft);
    if (!parsed.success) { setSettingsError(parsed.error.issues[0]?.message ?? "请检查打印设置"); return; }
    setSaving(true); setSettingsError("");
    try {
      const result = await request<SavedOrderPrintSettings>("/api/v1/admin/order-print-settings", { method: "PUT", body: JSON.stringify({ settings: parsed.data, revision: saved.revision }) });
      const settings = orderPrintSettingsSchema.parse(result.settings);
      setSaved({ ...result, settings }); setDraft(settings); setEditing(false);
      setSavedMessage("打印样式已保存，所有员工下次打印都会使用这套样式。");
    } catch (error) { setSettingsError(error instanceof Error ? error.message : "保存失败，请重试"); }
    finally { setSaving(false); }
  }

  useLayoutEffect(() => {
    // Measure at the physical print width, including headers and footers.
    // Long names or notes can require fewer than 15 items; never shrink or hide text.
    const sheets = rootRef.current?.querySelectorAll<HTMLElement>(".order-print") ?? [];
    let tallestContent = 0;
    for (let index = 0; index < pages.length; index++) {
      const sheet = sheets[index];
      if (!sheet) continue;
      const style = getComputedStyle(sheet);
      const available = sheet.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      if (available <= 0) continue; // Layout is unavailable in DOM-only tests.
      const content = sheet.querySelector<HTMLElement>(".order-print__content")!;
      tallestContent = Math.max(tallestContent, content.scrollHeight);
      const current = pages[index]!;
      // scrollHeight is independent of the modal's entrance transform. Reserve
      // two CSS pixels for browser rounding at the physical page boundary.
      if (current.items.length <= ITEMS_PER_PAGE && content.scrollHeight <= available - 2) continue;
      const next = pages.map((page) => ({ ...page, items: [...page.items] }));
      const page = next[index]!;
      if (page.note && page.items.length) {
        next.splice(index + 1, 0, { items: [], note: page.note });
        page.note = "";
      } else if (page.items.length > 1) {
        const item = page.items.pop()!;
        if (!next[index + 1]) next.push({ items: [], note: "" });
        next[index + 1]!.items.unshift(item);
      } else if (page.note.length > 1) {
        const characters = Array.from(page.note);
        const cut = Math.ceil(characters.length / 2);
        page.note = characters.slice(0, cut).join("");
        next.splice(index + 1, 0, { items: [], note: characters.slice(cut).join("") });
      } else {
        if (!overflow) {
          // eslint-disable-next-line react-hooks/set-state-in-effect -- Synchronize physical page capacity from DOM layout.
          setPagination({ source: order, template, pages, overflow: true });
        }
        return;
      }
      setPagination({ source: order, template, pages: next, overflow: false });
      return;
    }
    // Center the fullest document vertically, with the same starting position
    // on all sheets in this print batch. This margin is outside measured content.
    if (tallestContent > 0) rootRef.current?.style.setProperty("--order-print-content-height", `${tallestContent}px`);
  }, [order, template, pages, overflow]);

  useEffect(() => {
    const background = [...document.body.children]
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== rootRef.current)
      .map((element) => ({ element, inert: element.inert, ariaHidden: element.getAttribute("aria-hidden") }));
    for (const { element } of background) {
      element.inert = true;
      element.setAttribute("aria-hidden", "true");
    }
    return () => {
      for (const { element, inert, ariaHidden } of background) {
        if (!element.isConnected) continue;
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", ariaHidden);
      }
    };
  }, []);

  const customerName = order.customerSnapshot?.name || order.customerName || order.addressSnapshot.contactName;
  const contactName = order.customerSnapshot?.contactName || order.addressSnapshot.contactName || customerName;
  const phone = order.customerSnapshot?.phone || order.addressSnapshot.phone;
  const selfPickup = order.deliveryMethod === "SELF_PICKUP";
  const address = [order.addressSnapshot.province, order.addressSnapshot.city, order.addressSnapshot.district, order.addressSnapshot.detail].filter(Boolean).join("");
  const fulfillment = fulfillmentLabel(order.fulfillmentStatus, order.deliveryMethod).label;

  return createPortal(
    <div ref={rootRef} className={`order-print-root${documents ? " order-print-root--numbered" : ""}`}>
      <Modal title="订单打印预览" description="三联纸：进纸 241 × 140 毫米，撕边后 210 × 140 毫米。每张最多 15 行商品，点击打印后按当天顺序生成独立打印单号。请选择同尺寸纸张、100% 缩放，并关闭页眉页脚。" onClose={() => { if (!printBusy.current) onClose(); }}
        footer={<>{canEditSettings && <Button className="order-print-settings-toggle" variant="ghost" disabled={!ready || saving || printing} onClick={() => { setDraft(saved.settings); setSettingsError(""); setSavedMessage(""); setEditing(!editing); }}>{editing ? "返回打印预览" : "打印样式设置"}</Button>}<Button variant="secondary" disabled={printing} onClick={onClose}>取消</Button><Button disabled={!ready || editing || overflow || printing} loading={printing} onClick={() => void printDocuments()}><Printer aria-hidden="true" />{printing ? "正在生成打印单号" : "打印"}</Button></>}>
        {printError && <p className="order-print-settings-status" role="alert">{printError}</p>}
        <p className="order-print__number-required">请返回订单预览并点击“打印”，生成打印单号后再打印。</p>
        {!ready && <div className="order-print-settings-status" role={settingsError ? "alert" : "status"}><p>{settingsError || "正在加载已保存的打印样式…"}</p>{settingsError && <Button variant="secondary" onClick={() => void reloadSettings()}>重新加载打印样式</Button>}</div>}
        {ready && settingsError && !editing && <div className="order-print-settings-status" role="alert">{settingsError}<Button variant="secondary" onClick={() => void reloadSettings()}>重新加载打印样式</Button></div>}
        {savedMessage && <p className="order-print-settings-status" role="status">{savedMessage}</p>}
        {editing && <><OrderPrintSettingsEditor value={draft} onChange={setDraft} onSave={() => void saveSettings()} onCancel={() => { setDraft(saved.settings); setEditing(false); setSettingsError(""); }} saving={saving} error={settingsError} />{settingsError && <Button variant="secondary" onClick={() => void reloadSettings()}>重新加载已保存样式</Button>}</>}
        {overflow && <p className="order-print__overflow" role="alert">单条内容、页头或固定备注过长，无法放入一张纸。请检查内容或在打印样式设置中缩短文字。</p>}
        <div className="order-print-pages">
        {pages.map((page, pageIndex) => {
          const offset = pages.slice(0, pageIndex).reduce((sum, previous) => sum + previous.items.length, 0);
          const pageTotal = page.items.reduce((sum, item) => sum + item.lineTotalCents, 0);
          return <article key={pageIndex} className={`order-print order-print--${template.density} order-print--${template.borderStyle}`} style={{ "--order-print-font-size": `${template.fontSize}pt` } as CSSProperties} aria-label="订单打印内容">
          <div className="order-print__content">
          <header className="order-print__heading">
            <div className="order-print__title-line"><h1><span>{template.companyName}</span><span>{template.documentTitle}</span></h1><span className="order-print__document-number">打印单号：{documents?.[pageIndex]?.printNo ?? "待生成"}</span></div>
            {(template.address || template.fax || template.phones || template.copyLabels) && <div className="order-print__shop-details">
              <span>{template.address && `地址：${template.address}`}</span><span>{template.fax && `传真：${template.fax}`}</span>
              <span>{template.phones && `电话：${template.phones}`}</span><span className="order-print__copies">{template.copyLabels}</span>
            </div>}
            {template.headerNote && <p className="order-print__shop-text">{template.headerNote}</p>}
          </header>
          <div className="order-print__identity"><span>客户订单号：{order.orderNo}</span><span className="order-print__date">单据日期：{formatDateTime(order.createdAt).split(" ")[0]}</span></div>
          {order.status === "CANCELLED" && <div className="order-print__cancelled"><strong>已取消</strong>{order.cancellationReason && <span>取消原因：{order.cancellationReason}</span>}</div>}
          <dl className="order-print__customer">
            <div className="order-print__customer-name"><dt>客户</dt><dd>{customerName || "—"}</dd></div>
            <div className="order-print__contacts"><dt>联系人</dt><dd>{contactName || "—"}</dd><dt>电话</dt><dd>{phone || "—"}</dd></div>
            <div><dt>交付方式</dt><dd>{selfPickup ? "到店自提" : "送货"}</dd></div>
            <div className="order-print__customer-status"><dt>出货状态</dt><dd>{fulfillment}</dd></div>
            {!selfPickup && address && <div className="order-print__address"><dt>收货地址</dt><dd>{address}</dd></div>}
          </dl>
          <table className="order-print__table" aria-label="订单商品明细">
            <colgroup><col className="order-print__col-index" /><col className="order-print__col-product" /><col className="order-print__col-unit" /><col className="order-print__col-quantity" /><col className="order-print__col-price" /><col className="order-print__col-amount" /></colgroup>
            <thead><tr><th scope="col">行号</th><th scope="col">名称及规格</th><th scope="col">单位</th><th scope="col">数量</th><th scope="col">单价（元）</th><th scope="col">金额（元）</th></tr></thead>
            <tbody>{page.items.map((item, index) => <tr key={item.id}>
              <td>{offset + index + 1}</td>
              <td><strong>{item.productNameSnapshot}</strong>{item.specificationSnapshot && <small>{item.specificationSnapshot}</small>}</td>
              <td>{item.unitSnapshot}</td>
              <td className="order-print__number">{item.quantity}</td>
              <td className="order-print__number">{formatCny(item.unitPriceCents)}</td>
              <td className="order-print__number">{formatCny(item.lineTotalCents)}</td>
            </tr>)}</tbody>
          </table>
          <footer>
            <div className="order-print__summary">
              {template.fixedNote && <div className="order-print__note order-print__fixed-note"><strong>备注</strong><p>{template.fixedNote}</p></div>}
              {page.note && <div className="order-print__note"><strong>{page.items.length ? "订单备注" : "订单备注（续）"}</strong><p>{page.note}</p></div>}
              <div className="order-print__payment"><span>本单 {page.items.length} 项商品</span><span>合计数量：{page.items.reduce((sum, item) => sum + item.quantity, 0)}</span><strong>合计金额（元）：{formatCny(pageTotal)}</strong></div>
            </div>
            <div className="order-print__signatures">{template.signatureLabels.split(/[,，、\n]/).map((label) => label.trim().replace(/[：:]$/, "")).filter(Boolean).map((label, index) => <span key={index}>{label}：{label === "制单" ? order.createdByName || "" : ""}</span>)}</div>
          </footer>
          </div>
        </article>;
        })}
        </div>
      </Modal>
    </div>,
    document.body,
  );
}
